import { useState } from 'preact/hooks'
import type { JSX } from 'preact'
import { Trans, useTranslation } from 'react-i18next'
import {
  Button,
  InfoBox,
  LegalNotice,
  RequirementsSection,
  usePageLoader,
  Alert,
  Content,
  Header,
} from '@/components'
import {
  usePreventBackNavigation,
  useNavigationGuard,
} from '@/services/navigation-guard'
import { advanceToStep } from '@/services/navigation'
import { getToken, getAccessTokenType } from '@/services/session'
import { useSessionInitialization } from '@/hooks/useSessionInitialization'
import { useAccessTokenValidation } from '@/hooks/useAccessTokenValidation'
import { useHashTokenFlow } from '@/hooks/useHashTokenFlow'
import { AccessTokenInfoDtoType } from '@cencert/mob-signature'

function TermsPage() {
  const { t } = useTranslation()
  const { navigateTo } = useNavigationGuard()
  const { isLoading } = usePageLoader()

  useSessionInitialization()

  const { isFreeSignatureAvailable, isValidating } = useAccessTokenValidation()

  const { processHashToken, error: hashError } = useHashTokenFlow()

  usePreventBackNavigation()

  const [error, setError] = useState<string | null>(null)

  const handleGetStarted = async () => {
    try {
      setError(null)

      const token = getToken()
      const tokenType = getAccessTokenType()

      if (tokenType === AccessTokenInfoDtoType.HASH && token) {
        await processHashToken(token)
      } else {
        if (advanceToStep('add-document')) {
          navigateTo('/add-document')
        }
      }
    } catch {
      setError(hashError ?? t('errors.terms.apiFailure'))
    }
  }

  const getInfoBoxContent = ():
    | { title: string; description: string | JSX.Element }
    | { description: string | JSX.Element } => {
    const token = getToken()
    const tokenType = getAccessTokenType()

    if (tokenType === AccessTokenInfoDtoType.HASH) {
      return {
        description: t('terms.infoBox.hash.description'),
      }
    }

    if (token && isFreeSignatureAvailable) {
      return {
        title: t('terms.freeService.withTokenAndFreeSignatures.title'),
        description: (
          <Trans
            i18nKey='terms.freeService.withTokenAndFreeSignatures.description'
            components={{
              link1: <span />,
              link2: <span />,
            }}
          />
        ),
      }
    }

    if (token && !isFreeSignatureAvailable) {
      return {
        title: t('terms.freeService.withTokenNoFreeSignatures.title'),
        description: t(
          'terms.freeService.withTokenNoFreeSignatures.description'
        ),
      }
    }

    return {
      title: t('terms.freeService.noToken.title'),
      description: t('terms.freeService.noToken.description'),
    }
  }

  const infoBoxContent = getInfoBoxContent()

  return (
    <div className='bg-cencert-bg-gray min-h-screen'>
      <Header />

      <Content showSidebar={true}>
        <h1 className='text-heading-3 sm:text-heading-2 text-gray-900 mb-8 mt-4'>
          {t('terms.title')}
        </h1>

        {/* Error Display */}
        {error && (
          <Alert
            variant='error'
            message={error}
            onDismiss={() => setError(null)}
            className='mb-8'
          />
        )}

        {!isValidating && (
          <div className='mb-8'>
            <InfoBox {...infoBoxContent} />
          </div>
        )}

        <RequirementsSection showCencertCode={!getToken()} />

        <div className='flex justify-center  mt-6 mb-6 sm:mt-12 sm:mb-12'>
          <Button
            type='primary'
            size='lg'
            className='rounded-3xl tracking-[0.4px]'
            onClick={handleGetStarted}
            disabled={isLoading}
          >
            {t('terms.button')}
          </Button>
        </div>

        <div className='my-8'>
          <LegalNotice />
        </div>
      </Content>
    </div>
  )
}

export { TermsPage }
